import { useCallback, useMemo, useRef, useState } from 'react'

/**
 * Thin wrapper around the browser's built-in speech recognition
 * (SpeechRecognition / webkitSpeechRecognition) that turns live speech into
 * timestamped caption segments while a screen recording is in progress.
 *
 * Only Chrome/Edge ship this today (it's undefined everywhere else — Firefox,
 * Safari). Two important limitations, both inherent to the browser API, not
 * this code:
 *   1. It always listens to the OS microphone directly — there's no way to
 *      hand it a MediaStream, so it can only caption speech the mic picks
 *      up, never tab/system audio alone. Callers should only offer this
 *      when a mic is actually in use.
 *   2. It calls out to a cloud recognition service (Google's, in Chrome),
 *      so it needs an active internet connection during recording.
 *
 * Segment timestamps are computed the same "accumulate active time across
 * pause/resume" way useScreenRecorder times the recording itself (see its
 * `pausedAccumRef`/`startedAtRef` pattern), so captions line up with the
 * final video's timeline even if the user paused partway through.
 */
const useCaptionRecognition = () => {
  const SpeechRecognitionCtor = useMemo(
    () => (typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null),
    []
  )
  const supported = !!SpeechRecognitionCtor

  const [listening, setListening] = useState(false)

  const recognitionRef = useRef(null)
  const shouldListenRef = useRef(false) // true whenever the caller wants captions running — drives auto-restart after Chrome's silence timeout
  const startedAtRef = useRef(0)
  const pausedAccumRef = useRef(0) // active (non-paused) ms accumulated before the current run
  const segmentsRef = useRef([])
  const firstSeenRef = useRef(new Map()) // SpeechRecognition result index -> ms first observed (interim)

  const elapsedAt = useCallback((ms) => Math.max(0, (pausedAccumRef.current + (ms - startedAtRef.current)) / 1000), [])

  const attach = useCallback(() => {
    const recognition = new SpeechRecognitionCtor()
    recognition.continuous = true
    recognition.interimResults = true
    try { recognition.lang = navigator.language || 'en-US' } catch { /* noop */ }

    recognition.onresult = (event) => {
      const now = Date.now()
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (!firstSeenRef.current.has(i)) firstSeenRef.current.set(i, now)
        if (result.isFinal) {
          const text = (result[0]?.transcript || '').trim()
          const firstSeen = firstSeenRef.current.get(i) ?? now
          firstSeenRef.current.delete(i)
          if (text) {
            segmentsRef.current = [
              ...segmentsRef.current,
              { id: `cap_${startedAtRef.current}_${i}`, start: elapsedAt(firstSeen), end: elapsedAt(now), text },
            ]
          }
        }
      }
    }

    recognition.onerror = () => {
      // 'no-speech' / 'aborted' fire constantly during ordinary pauses in
      // speech — not real failures. onend (below) handles restarting either
      // way, so there's nothing to surface to the caller here.
    }

    recognition.onend = () => {
      if (shouldListenRef.current) {
        // Chrome stops the recognizer after a period of silence even in
        // continuous mode — restart it transparently as long as we're still
        // supposed to be listening.
        try { recognition.start() } catch { /* already starting somewhere — ignore */ }
      } else {
        setListening(false)
      }
    }

    recognitionRef.current = recognition
    return recognition
  }, [SpeechRecognitionCtor, elapsedAt])

  const reset = useCallback(() => {
    segmentsRef.current = []
    firstSeenRef.current = new Map()
  }, [])

  /** Starts listening. `startedAtMs` should be the same reference point (Date.now()) the caller times the recording itself from. */
  const start = useCallback((startedAtMs) => {
    if (!supported) return
    reset()
    startedAtRef.current = startedAtMs
    pausedAccumRef.current = 0
    shouldListenRef.current = true
    const recognition = attach()
    try {
      recognition.start()
      setListening(true)
    } catch {
      shouldListenRef.current = false
    }
  }, [supported, attach, reset])

  const pause = useCallback(() => {
    if (!shouldListenRef.current) return
    pausedAccumRef.current += Date.now() - startedAtRef.current
    shouldListenRef.current = false
    if (recognitionRef.current) {
      try { recognitionRef.current.stop() } catch { /* noop */ }
    }
  }, [])

  const resume = useCallback(() => {
    if (!supported) return
    startedAtRef.current = Date.now()
    shouldListenRef.current = true
    const recognition = attach()
    try {
      recognition.start()
      setListening(true)
    } catch {
      shouldListenRef.current = false
    }
  }, [supported, attach])

  const stop = useCallback(() => {
    shouldListenRef.current = false
    if (recognitionRef.current) {
      try { recognitionRef.current.stop() } catch { /* noop */ }
    }
  }, [])

  const getSegments = useCallback(() => segmentsRef.current, [])

  return { supported, listening, start, pause, resume, stop, getSegments, reset }
}

export default useCaptionRecognition
